/**
 * 근거 시트 (시안 03 E1). 핸들 → "{멤버}{이/가} 파고든 인물 · {책}" → 원문 상자 → 풀이 → "출처 · {출처}" → 닫기.
 *
 * 경로로 엽니다(…/evidence/:evidenceKey). 닫기는 부르는 쪽이 정하는 뒤로 가기라, 안드로이드
 * 뒤로 버튼도 시트만 닫습니다.
 */
import { useEffect, useState } from 'react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { BottomSheet } from './BottomSheet.jsx';
import { Button } from './Button.jsx';
import { errorMessageOf } from '../api/client.js';
import { fetchEvidence } from '../api/chat.js';

export function EvidenceSheet({ evidenceKey, onClose }) {
    const [state, setState] = useState({ status: 'loading', evidence: null, error: null });

    useEffect(() => {
        if (!evidenceKey) {
            return undefined;
        }
        const controller = new AbortController();
        setState({ status: 'loading', evidence: null, error: null });
        fetchEvidence(evidenceKey, { signal: controller.signal })
            .then(({ evidence }) => {
                setState({ status: 'ready', evidence, error: null });
            })
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setState({
                        status: 'error',
                        evidence: null,
                        error: errorMessageOf(error, '근거를 불러오지 못했어요.'),
                    });
                }
            });
        return () => {
            controller.abort();
        };
    }, [evidenceKey]);

    const { evidence } = state;
    const memberName = evidence ? getMember(evidence.memberKey)?.name : null;

    return (
        <BottomSheet open={Boolean(evidenceKey)} onClose={onClose} label="근거">
            {state.status === 'loading' ? (
                <p className="m-0 py-6 text-center text-caption text-text-secondary">근거를 펼치는 중이에요.</p>
            ) : null}
            {state.status === 'error' ? (
                <p role="alert" className="m-0 py-6 text-center text-caption text-error">
                    {state.error}
                </p>
            ) : null}
            {evidence ? (
                <>
                    {evidence.original ? (
                        <>
                            <span className="text-[13px] font-semibold text-accent-text">
                                {withJosa(memberName, '이', '가')} 파고든 인물 · {evidence.figure}
                            </span>
                            <div className="rounded-card bg-surface-sunken p-4 font-serif text-[18px] leading-[30px] font-bold">
                                {evidence.original}
                            </div>
                        </>
                    ) : (
                        // 현대 지식 · 향 지식 · 곡 카드는 원문 없이 사실 한 줄(D48)
                        <span className="text-[13px] font-semibold text-accent-text">
                            {withJosa(memberName, '이', '가')} 찾아본 것 · {evidence.figure}
                        </span>
                    )}
                    <p className="m-0 text-[15px] leading-6">{evidence.gloss}</p>
                    <span className="text-[13px] text-text-secondary">
                        출처 · {evidence.source}
                        {evidence.original ? '' : ` · ${evidence.gradeLabel}`}
                    </span>
                </>
            ) : null}
            <Button variant="secondary" fullWidth className="mt-1 border-hairline text-text-primary" onClick={onClose}>
                닫기
            </Button>
        </BottomSheet>
    );
}
